import {
  getFirestore,
  doc,
  updateDoc,
  Timestamp,
  query,
  collection,
  where,
  getDocs,
  deleteDoc
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'

// Vuex-toiminnot, jotka päivittävät Firestorea

// Turvallinen numeromuunnos: hyväksyy myös merkkijonot, palauttaa oletuksen jos ei kelvollinen
const toNum = (v, fallback = 0) => {
  if (v === null || v === undefined) return fallback
  const n = Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : fallback
}

const actions = {
  // Vaihtaa käyttäjän salasanan kutsumalla backend-funktiota (vaatii kirjautumisen)
  async updatePass({}, payload) {
    const auth = getAuth()
    const user = auth.currentUser
    if (!user) throw new Error('Not authenticated')

    const idToken = await user.getIdToken(true)
    const url = 'https://europe-west1-ows-webedi-prod.cloudfunctions.net/app/api/updatepass'
    const body = JSON.stringify({ uid: payload.uid, password: payload.pass })

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body
    })

    if (!res.ok) {
      throw new Error(`updatePass failed (${res.status})`)
    }
  },

  // Päivittää käyttäjäprofiilin peruskentät
  async updateUser({}, payload) {
    const db = getFirestore()
    const q = query(collection(db, 'users'), where('username', '==', payload.username))
    const querySnapshot = await getDocs(q)
    const uid = querySnapshot.docs[0].id
    const userRef = doc(db, 'users', uid)

    await updateDoc(userRef, {
      email: payload.email,
      firstname: payload.firstname,
      lastname: payload.lastname,
      phone: payload.phone,
      disabled: payload.disabled
    })
  },

  // Tallentaa taulun otsikkosarakkeet (käyttäjäkohtaiset asetukset)
  async updateUserHeaders({}, payload) {
    const auth = getAuth()
    const db = getFirestore()
    const userRef = doc(db, 'users', auth.currentUser.uid)
    await updateDoc(userRef, { headers: payload.headers })
  },

  // Tallentaa tilausten listan otsikkosarakkeet
  async updateUserOrdersHeaders({}, payload) {
    const auth = getAuth()
    const db = getFirestore()
    const userRef = doc(db, 'users', auth.currentUser.uid)
    await updateDoc(userRef, { ordersHeaders: payload.headers })
  },

  // Päivittää lokalisaation (käyttöliittymän kieli)
  async updateLocalization({ commit }, payload) {
    const auth = getAuth()
    const db = getFirestore()
    const userRef = doc(db, 'users', auth.currentUser.uid)
    await updateDoc(userRef, { lang: payload.lang })
    return true
  },

  // Päivittää tilauksen (rivit, päivämäärät, huomautukset)
  async updateOrder({}, payload) {
    const db = getFirestore()
    const orderRef = doc(db, 'orders', payload.orderid)

    // Normalisoidaan rivit: varmistetaan numerot ja muutetaan päivämäärä Firestore Timestampiksi
    const rows = payload.rows.map(row => {
      // deliverydate → Timestamp (hyväksyy Timestampin, Date/ISO-merkkijonon tai sekunnit)
      const seconds =
        row?.deliverydate?.seconds ??
        Math.floor(new Date(row.deliverydate).getTime() / 1000)
      const deliveryTS = Timestamp.fromMillis(seconds * 1000)

      // Määrien normalisointi:
      // confirmed_amount ← order_amount (jos puuttuu)
      // delivery_amount  ← confirmed_amount (jos puuttuu)
      const ordered = toNum(row.order_amount, 0)
      const confirmed = toNum(row.confirmed_amount, ordered)
      const delivered = toNum(row.delivery_amount, confirmed)

      return {
        ...row,
        deliverydate: deliveryTS,
        order_amount: ordered.toFixed(4),
        confirmed_amount: confirmed.toFixed(4),
        // Kirjoitetaan aina delivery_amount (myös 0.0000), jotta kenttä ei katoa
        delivery_amount: delivered.toFixed(4)
      }
    })

    // Ylätason confirmeddeliverydate → Timestamp (päivitetään vain jos annettu)
    const updatePayload = {
      ordernotes: payload.ordernotes,
      orderrows: rows
    }

    if (payload.confirmeddeliverydate) {
      const cddSec =
        payload.confirmeddeliverydate.seconds ??
        Math.floor(new Date(payload.confirmeddeliverydate).getTime() / 1000)
      updatePayload.confirmeddeliverydate = Timestamp.fromMillis(cddSec * 1000)
    }

    await updateDoc(orderRef, updatePayload)
  },

  // Päivittää organisaation tiedot
  async updateOrg({}, payload) {
    const db = getFirestore()
    const q = query(collection(db, 'organisations'), where('partyid', '==', payload.oldpartyid))
    const querySnapshot = await getDocs(q)
    const oid = querySnapshot.docs[0].id
    const organisationRef = doc(db, 'organisations', oid)

    await updateDoc(organisationRef, {
      name: payload.name,
      orgtype: payload.orgtype,
      partyid: payload.newpartyid,
      info: payload.info
    })
  },

  // Poistaa ilmoituksen otsikon perusteella
  async updateNotifications({}, payload) {
    const db = getFirestore()
    const q = query(collection(db, 'notifications'), where('title', '==', payload.title))
    const querySnapshot = await getDocs(q)
    const id = querySnapshot.docs[0].id
    await deleteDoc(doc(db, 'notifications', id))
  },

  // Nollaa tilauksen aikaleimaliput (debug/huolto)
  async dropTimestamps({}, payload) {
    const db = getFirestore()
    const orderRef = doc(db, 'orders', payload.orderid)
    await updateDoc(orderRef, {
      confirmed_timestamp: false,
      delivered_timestamp: false
    })
  }
}

export default {
  namespaced: true,
  actions
}
